/* =============================================================
   USASchoolsDirectory — United Dream Mongolia
   96 US schools across 8 cities — filterable, price-first default
   ============================================================= */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useVisaQuiz } from "@/contexts/VisaQuizContext";
import {
  MapPin,
  DollarSign,
  Clock,
  Award,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  GraduationCap,
  BookOpen,
  BadgeCheck,
  Sparkles,
  FileText,
} from "lucide-react";
import { useConsultation } from "@/contexts/ConsultationModalContext";
import {
  USA_SCHOOLS,
  CITIES,
  SCHOOL_TYPE_LABELS,
  type CityFilter,
  type SchoolType,
} from "@/lib/usaSchools";

/* ── City colour palette ──────────────────────────────────── */
const CITY_COLORS: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  "Чикаго":       { bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200",    dot: "bg-blue-500"    },
  "Лос-Анжелес":  { bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200",   dot: "bg-amber-500"   },
  "Нью-Йорк":     { bg: "bg-rose-50",    text: "text-rose-700",    border: "border-rose-200",    dot: "bg-rose-500"    },
  "Бостон":       { bg: "bg-purple-50",  text: "text-purple-700",  border: "border-purple-200",  dot: "bg-purple-500"  },
  "Вашингтон":    { bg: "bg-indigo-50",  text: "text-indigo-700",  border: "border-indigo-200",  dot: "bg-indigo-500"  },
  "Сиетл":        { bg: "bg-cyan-50",    text: "text-cyan-700",    border: "border-cyan-200",    dot: "bg-cyan-500"    },
  "Денвер":       { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
  "Канзас Сити":  { bg: "bg-orange-50",  text: "text-orange-700",  border: "border-orange-200",  dot: "bg-orange-500"  },
};

/* ── School-type colours ──────────────────────────────────── */
const TYPE_COLORS: Record<SchoolType, { bg: string; text: string; border: string }> = {
  community_college: { bg: "bg-green-50",  text: "text-green-700",  border: "border-green-200"  },
  language_school:   { bg: "bg-sky-50",    text: "text-sky-700",    border: "border-sky-200"    },
  university_eli:    { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200" },
};

/* ── Duration label helper ────────────────────────────────── */
function durationLabel(weeks: number): string {
  if (weeks <= 2)  return "2 долоо хоногоос";
  if (weeks <= 4)  return "4 долоо хоногоос";
  if (weeks <= 8)  return "8 долоо хоногоос";
  if (weeks <= 12) return "3 сараас";
  return "6 сараас";
}

/* ── School card ────────────────────────────────────────────────── */
function SchoolCard({ school }: { school: typeof USA_SCHOOLS[0] }) {
  const [expanded, setExpanded] = useState(false);
  const openQuiz = useVisaQuiz();
  const cityColor = CITY_COLORS[school.cityMn] ?? CITY_COLORS["Чикаго"];
  const typeColor = TYPE_COLORS[school.schoolType];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 transition-all overflow-hidden"
    >
      {/* Top accent bar */}
      <div className={`h-1 w-full ${cityColor.dot}`} />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className={`w-11 h-11 rounded-xl ${cityColor.bg} border ${cityColor.border} flex items-center justify-center flex-shrink-0`}>
            <GraduationCap className={`w-5 h-5 ${cityColor.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="type-label text-[#0A1628] text-sm leading-tight mb-1.5 pr-2">{school.name}</h4>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${cityColor.bg} ${cityColor.text} border ${cityColor.border}`}
                style={{ fontFamily: "var(--font-body)" }}>
                <MapPin className="w-2.5 h-2.5" />
                {school.cityMn}
              </span>
              {/* Accreditation badge in header */}
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-amber-50 text-amber-700 border border-amber-200"
                style={{ fontFamily: "var(--font-body)" }}>
                <Award className="w-2.5 h-2.5" />
                {school.accreditation.split(",")[0].trim()}
              </span>
            </div>
          </div>
        </div>

        {/* Badges row */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {/* School type */}
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${typeColor.bg} ${typeColor.text} ${typeColor.border}`}
            style={{ fontFamily: "var(--font-body)" }}>
            {school.schoolType === "community_college" && "🏫"}
            {school.schoolType === "language_school" && "📚"}
            {school.schoolType === "university_eli" && "🎓"}
            {SCHOOL_TYPE_LABELS[school.schoolType]}
          </span>
          {/* Scholarship badge */}
          {school.hasScholarship && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-yellow-50 text-yellow-700 border border-yellow-200"
              style={{ fontFamily: "var(--font-body)" }}>
              <Sparkles className="w-2.5 h-2.5" />
              Тэтгэлэг боломжтой
            </span>
          )}
          {/* No English test badge */}
          {school.noEnglishTest && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-green-50 text-green-700 border border-green-200"
              style={{ fontFamily: "var(--font-body)" }}>
              <BadgeCheck className="w-2.5 h-2.5" />
              IELTS шаардахгүй
            </span>
          )}
        </div>

        {/* Price + Duration */}
        <div className="flex items-center gap-4 mb-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
          <div className="flex items-center gap-1.5">
            <DollarSign className="w-4 h-4 text-[#1E6FD9]" />
            <div>
              <div className="type-heading text-[#0A1628] text-base">${school.tuitionPerMonth.toLocaleString()}</div>
              <div className="type-body text-slate-400 text-[10px]">сард (ойролцоо)</div>
            </div>
          </div>
          <div className="w-px h-8 bg-slate-200" />
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-slate-400" />
            <div>
              <div className="type-body text-slate-600 text-xs leading-tight">{durationLabel(school.minDurationWeeks)}</div>
              <div className="type-body text-slate-400 text-[10px]">хамгийн богино</div>
            </div>
          </div>
        </div>

        {/* Programs (collapsed) */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {school.programs.slice(0, 3).map((p) => (
            <span key={p} className="px-2 py-1 rounded-lg bg-blue-50 text-[#1E6FD9] border border-blue-100"
              style={{ fontFamily: "var(--font-body)", fontSize: "0.7rem" }}>
              {p}
            </span>
          ))}
          {school.programs.length > 3 && (
            <span className="px-2 py-1 rounded-lg bg-slate-100 text-slate-500"
              style={{ fontFamily: "var(--font-body)", fontSize: "0.7rem" }}>
              +{school.programs.length - 3}
            </span>
          )}
        </div>

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
        >
          <span className="type-body text-[#1E6FD9] text-xs">
            {expanded ? "Хураах" : "Дэлгэрэнгүй харах"}
          </span>
          {expanded
            ? <ChevronUp className="w-4 h-4 text-[#1E6FD9]" />
            : <ChevronDown className="w-4 h-4 text-[#1E6FD9]" />}
        </button>

        {/* Expanded content */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="pt-3 space-y-3 border-t border-slate-100 mt-2">
                {/* Address */}
                <div className="flex items-start gap-2">
                  <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span className="type-body text-slate-500 text-xs">{school.address}</span>
                </div>

                {/* All programs */}
                <div>
                  <div className="type-label text-[#0A1628] text-xs mb-1.5 flex items-center gap-1">
                    <BookOpen className="w-3 h-3" /> Хөтөлбөрүүд
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {school.programs.map((p) => (
                      <span key={p} className="px-2 py-1 rounded-lg bg-blue-50 text-[#1E6FD9] border border-blue-100"
                        style={{ fontFamily: "var(--font-body)", fontSize: "0.7rem" }}>
                        {p}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Accreditation — full list */}
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-100">
                  <Award className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="type-label text-amber-800 text-[10px] mb-0.5">Магадлан итгэмжлэл</div>
                    <div className="type-body text-amber-700 text-xs">{school.accreditation}</div>
                  </div>
                </div>

                {/* Highlights */}
                <div>
                  <div className="type-label text-[#0A1628] text-xs mb-1.5">Онцлог давуу талууд</div>
                  <ul className="space-y-1">
                    {school.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2 type-body text-slate-600 text-xs">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#1E6FD9] mt-1.5 flex-shrink-0" />
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Website */}
                <div className="flex items-center gap-2">
                  <ExternalLink className="w-3.5 h-3.5 text-[#1E6FD9]" />
                  <span className="type-body text-[#1E6FD9] text-xs">{school.website}</span>
                </div>

                {/* Visa application button */}
                <button
                  onClick={() => openQuiz({ id: "usa", flag: "🇺🇸", name: "АНУ" })}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#1E6FD9] text-white hover:bg-[#1a5fc4] transition-all type-label text-sm shadow-sm mt-1"
                >
                  <FileText className="w-4 h-4" />
                  Виз мэдүүлэх
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

/* ── Main directory ───────────────────────────────────────── */
type SortKey = "price_asc" | "price_desc" | "rating";
type DurationFilter = "all" | "short" | "medium" | "6month" | "12month" | "24month";

export default function USASchoolsDirectory() {
  const { openConsultation } = useConsultation();

  /* Filter state */
  const [activeCity, setActiveCity]           = useState<CityFilter>("Бүгд");
  const [sortBy, setSortBy]                   = useState<SortKey>("price_asc");
  const [filterScholarship, setFilterScholarship] = useState(false);
  const [filterNoTest, setFilterNoTest]       = useState(false);
  const [filterType, setFilterType]           = useState<SchoolType | "all">("all");
  const [filterDuration, setFilterDuration]   = useState<DurationFilter>("all");

  /* Filtering */
  const filtered = USA_SCHOOLS
    .filter((s) => activeCity === "Бүгд" || s.cityMn === activeCity)
    .filter((s) => !filterScholarship || s.hasScholarship)
    .filter((s) => !filterNoTest || s.noEnglishTest)
    .filter((s) => filterType === "all" || s.schoolType === filterType)
    .filter((s) => {
      if (filterDuration === "all")     return true;
      if (filterDuration === "short")   return s.minDurationWeeks <= 4;       // ≤4 долоо хоног
      if (filterDuration === "medium")  return s.minDurationWeeks <= 12;      // ≤3 сар
      if (filterDuration === "6month")  return s.minDurationWeeks <= 24;      // ≤6 сар
      if (filterDuration === "12month") return s.minDurationWeeks <= 52;      // ≤12 сар
      return true; // 24month — бүгд
    })
    .sort((a, b) => {
      if (sortBy === "rating")     return b.rating - a.rating;
      if (sortBy === "price_asc")  return a.tuitionPerMonth - b.tuitionPerMonth;
      return b.tuitionPerMonth - a.tuitionPerMonth;
    });

  const cityCount = (city: CityFilter) =>
    city === "Бүгд" ? USA_SCHOOLS.length : USA_SCHOOLS.filter((s) => s.cityMn === city).length;

  return (
    <div className="mt-10">
      {/* Section header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#1E6FD9]/10 flex items-center justify-center">
          <GraduationCap className="w-5 h-5 text-[#1E6FD9]" />
        </div>
        <div>
          <h3 className="type-heading text-[#0A1628] text-xl">АНУ-ын Сургуулийн Танилцуулга</h3>
          <p className="type-body text-slate-500 text-sm">
            F-1 оюутны визтэй суралцах боломжтой {USA_SCHOOLS.length} сургууль — 8 хот
          </p>
        </div>
      </div>

      {/* City filter tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {CITIES.map((city) => {
          const color = city !== "Бүгд" ? CITY_COLORS[city] : null;
          const isActive = activeCity === city;
          return (
            <button
              key={city}
              onClick={() => setActiveCity(city)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl border transition-all type-body text-sm ${
                isActive
                  ? "bg-[#0A1628] text-white border-[#0A1628] shadow-md"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
              }`}
            >
              {color && (
                <span className={`w-2 h-2 rounded-full ${isActive ? "bg-white/60" : color.dot}`} />
              )}
              {city}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                isActive ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
              }`}>
                {cityCount(city)}
              </span>
            </button>
          );
        })}
      </div>

      {/* Advanced filters row */}
      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-xl bg-slate-50 border border-slate-100">
        {/* School type */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="type-body text-slate-400 text-xs">Төрөл:</span>
          {([
            { key: "all" as const, label: "Бүгд" },
            { key: "community_college" as const, label: "🏫 Community" },
            { key: "language_school" as const, label: "📚 Хэлний" },
            { key: "university_eli" as const, label: "🎓 Их сургууль" },
          ]).map((opt) => (
            <button key={opt.key} onClick={() => setFilterType(opt.key)}
              className={`px-2.5 py-1 rounded-lg border text-xs transition-all type-body ${
                filterType === opt.key
                  ? "bg-[#1E6FD9] text-white border-[#1E6FD9]"
                  : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
              }`}>
              {opt.label}
            </button>
          ))}
        </div>

        <div className="w-px h-6 bg-slate-200 hidden sm:block" />

        {/* Duration */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="type-body text-slate-400 text-xs">Хугацаа:</span>
          {([
            { key: "all" as const, label: "Бүгд" },
            { key: "short" as const, label: "≤4 долоо хоног" },
            { key: "medium" as const, label: "≤3 сар" },
            { key: "6month" as const, label: "≤6 сар" },
            { key: "12month" as const, label: "≤12 сар" },
            { key: "24month" as const, label: "≤24 сар" },
          ]).map((opt) => (
            <button key={opt.key} onClick={() => setFilterDuration(opt.key)}
              className={`px-2.5 py-1 rounded-lg border text-xs transition-all type-body ${
                filterDuration === opt.key
                  ? "bg-[#1E6FD9] text-white border-[#1E6FD9]"
                  : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
              }`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Checkbox filters + sort */}
      <div className="flex flex-wrap items-center gap-4 mb-5">
        {/* Checkboxes */}
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" checked={filterScholarship}
            onChange={(e) => setFilterScholarship(e.target.checked)}
            className="w-4 h-4 rounded accent-yellow-500" />
          <span className="type-body text-slate-600 text-sm flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5 text-yellow-500" />
            Тэтгэлэг боломжтой
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" checked={filterNoTest}
            onChange={(e) => setFilterNoTest(e.target.checked)}
            className="w-4 h-4 rounded accent-green-500" />
          <span className="type-body text-slate-600 text-sm flex items-center gap-1">
            <BadgeCheck className="w-3.5 h-3.5 text-green-500" />
            IELTS/TOEFL шаардахгүй
          </span>
        </label>

        <div className="ml-auto flex items-center gap-2">
          <span className="type-body text-slate-400 text-xs">Эрэмбэлэх:</span>
          {([
            { key: "price_asc" as const, label: "Хямд эхэлж" },
            { key: "price_desc" as const, label: "Үнэтэй эхэлж" },
            { key: "rating" as const, label: "Үнэлгээгээр" },
          ]).map((opt) => (
            <button key={opt.key} onClick={() => setSortBy(opt.key)}
              className={`px-3 py-1.5 rounded-lg border text-xs transition-all type-body ${
                sortBy === opt.key
                  ? "bg-[#1E6FD9] text-white border-[#1E6FD9]"
                  : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
              }`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Results count */}
      <p className="type-body text-slate-400 text-xs mb-4">
        {filtered.length} сургууль олдлоо
        {filterScholarship && " · тэтгэлэгтэй"}
        {filterNoTest && " · IELTS шаардахгүй"}
      </p>

      {/* School grid */}
      <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AnimatePresence mode="popLayout">
          {filtered.map((school) => (
            <SchoolCard key={school.id} school={school} />
          ))}
        </AnimatePresence>
      </motion.div>

      {filtered.length === 0 && (
        <div className="text-center py-16 text-slate-400">
          <GraduationCap className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="type-body text-sm">Шүүлтүүрт тохирох сургууль олдсонгүй</p>
          <button onClick={() => { setFilterScholarship(false); setFilterNoTest(false); setFilterType("all"); setFilterDuration("all"); setActiveCity("Бүгд"); setSortBy("price_asc"); }}
            className="mt-3 text-[#1E6FD9] text-sm underline type-body">
            Шүүлтүүр цэвэрлэх
          </button>
        </div>
      )}

      {/* Bottom CTA */}
      <div className="mt-8 p-5 rounded-2xl bg-gradient-to-r from-[#0A1628] to-[#1a3a6b] text-center">
        <div className="type-label text-white text-base mb-1">
          Аль сургуульд элсэх нь таньд тохиромжтой вэ?
        </div>
        <p className="type-body text-white/60 text-sm mb-4">
          Манай зөвлөхүүд таны нөхцөл байдалд тохирсон хамгийн тохиромжтой сургуулийг сонгоход туслана
        </p>
        <a
          href="#" onClick={(e) => { e.preventDefault(); openConsultation(); }}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#1E6FD9] text-white hover:bg-[#1a5fc4] transition-all type-label text-sm shadow-lg shadow-[#1E6FD9]/30"
        >
          Үнэгүй зөвлөгөө авах
        </a>
      </div>
    </div>
  );
}
